import { useState, useEffect, useMemo } from 'react';
import { llcApi, bankApi } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Pencil, Trash2, Building2, LayoutGrid, List, ArrowUpDown, ArrowUp, ArrowDown, Upload, X, Image, ClipboardList, FileDown, FileCheck2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatPhoneNumber = (value) => {
  if (!value) return '';
  const digits = value.replace(/\D/g, '');
  const truncated = digits.slice(0, 10);
  if (truncated.length === 0) return '';
  if (truncated.length <= 3) return `(${truncated}`;
  if (truncated.length <= 6) return `(${truncated.slice(0, 3)}) ${truncated.slice(3)}`;
  return `(${truncated.slice(0, 3)}) ${truncated.slice(3, 6)}-${truncated.slice(6)}`;
};

const initialFormData = {
  company_name: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  zip_code: '',
  ein: '',
  phone: '',
  bank_id: '',
  account_number: '',
  next_check_number: 1001,
  issues_1099: true,
};

export const LLCManagementPage = () => {
  const [llcs, setLlcs] = useState([]);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLLC, setEditingLLC] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [submitting, setSubmitting] = useState(false);
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('llcMgmt_viewMode') || 'grid';
  });
  const [sortField, setSortField] = useState('company_name');
  const [sortDir, setSortDir] = useState('asc');

  // LLC Checks Modal
  const [showChecks, setShowChecks] = useState(false);
  const [checksLlcName, setChecksLlcName] = useState('');
  const [llcChecks, setLlcChecks] = useState([]);
  const [checksLoading, setChecksLoading] = useState(false);
  const [checksSortCol, setChecksSortCol] = useState('date');
  const [checksSortDir, setChecksSortDir] = useState('desc');

  // Branding state
  const [logoPreview, setLogoPreview] = useState(null);
  const [sigPreview, setSigPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingSig, setUploadingSig] = useState(false);

  const openLlcChecks = async (llcId, llcName) => {
    setChecksLlcName(llcName);
    setShowChecks(true);
    setChecksLoading(true);
    try {
      const res = await llcApi.getChecks(llcId);
      setLlcChecks(res.data);
    } catch {
      toast.error('Failed to load checks');
    }
    setChecksLoading(false);
  };

  const sortedLlcChecks = useMemo(() => {
    return [...llcChecks].sort((a, b) => {
      const dir = checksSortDir === 'asc' ? 1 : -1;
      switch (checksSortCol) {
        case 'ref': {
          const refA = a.type === 'check' ? (a.ref || 0) : 0;
          const refB = b.type === 'check' ? (b.ref || 0) : 0;
          return dir * (refA - refB);
        }
        case 'date': return dir * (new Date(a.date) - new Date(b.date));
        case 'payee': return dir * (a.payee || '').localeCompare(b.payee || '');
        case 'memo': return dir * (a.memo || '').localeCompare(b.memo || '');
        case 'amount': return dir * ((a.amount || 0) - (b.amount || 0));
        case 'status': return dir * (a.status || '').localeCompare(b.status || '');
        default: return 0;
      }
    });
  }, [llcChecks, checksSortCol, checksSortDir]);

  const toggleChecksSort = (col) => {
    if (checksSortCol === col) {
      setChecksSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setChecksSortCol(col);
      setChecksSortDir('asc');
    }
  };

  const ChecksSortHeader = ({ col, children, className = '' }) => (
    <th className={`cursor-pointer select-none hover:bg-slate-100 ${className}`} onClick={() => toggleChecksSort(col)}>
      <span className="inline-flex items-center gap-1">
        {children}
        {checksSortCol === col ? (checksSortDir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-30" />}
      </span>
    </th>
  );

  const handleViewMode = (mode) => {
    setViewMode(mode);
    localStorage.setItem('llcMgmt_viewMode', mode);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortedLLCs = [...llcs].sort((a, b) => {
    let aVal = a[sortField] || '';
    let bVal = b[sortField] || '';
    if (sortField === 'next_check_number') {
      aVal = Number(aVal) || 0;
      bVal = Number(bVal) || 0;
    } else if (sortField === 'bank_name_display') {
      aVal = a.bank_id ? getBankName(a.bank_id) : (a.bank_name || '');
      bVal = b.bank_id ? getBankName(b.bank_id) : (b.bank_name || '');
    } else {
      aVal = String(aVal).toLowerCase();
      bVal = String(bVal).toLowerCase();
    }
    if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 text-slate-400" />;
    return sortDir === 'asc' 
      ? <ArrowUp className="w-3 h-3 text-white" /> 
      : <ArrowDown className="w-3 h-3 text-white" />;
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [llcsResponse, banksResponse] = await Promise.all([
        llcApi.list(),
        bankApi.list()
      ]);
      setLlcs(llcsResponse.data);
      setBanks(banksResponse.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'next_check_number' ? parseInt(value) || 1001 : name === 'phone' ? formatPhoneNumber(value) : value,
    }));
  };

  const handleBankChange = (bankId) => {
    const bank = banks.find(b => b.id === bankId);
    setFormData((prev) => ({
      ...prev,
      bank_id: bankId,
      // Also set legacy fields for compatibility
      bank_name: bank?.name || '',
      bank_address: bank?.address || '',
      bank_city_state_zip: bank?.city_state_zip || '',
      bank_phone: bank?.phone || '',
      routing_number: bank?.outbound_routing || bank?.routing_number || '',
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Get bank details to send with LLC
      const selectedBank = banks.find(b => b.id === formData.bank_id);
      const submitData = {
        ...formData,
        bank_name: selectedBank?.name || '',
        bank_address: selectedBank?.address || '',
        bank_city_state_zip: selectedBank?.city_state_zip || '',
        bank_phone: selectedBank?.phone || '',
        routing_number: selectedBank?.outbound_routing || selectedBank?.routing_number || '',
      };

      if (editingLLC) {
        await llcApi.update(editingLLC.id, submitData);
        toast.success('LLC updated successfully');
      } else {
        await llcApi.create(submitData);
        toast.success('LLC created successfully');
      }
      setDialogOpen(false);
      setEditingLLC(null);
      setFormData(initialFormData);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (llc) => {
    setEditingLLC(llc);
    setFormData({
      company_name: llc.company_name,
      address_line1: llc.address_line1,
      address_line2: llc.address_line2 || '',
      city: llc.city,
      state: llc.state,
      zip_code: llc.zip_code,
      ein: llc.ein,
      phone: formatPhoneNumber(llc.phone || ''),
      bank_id: llc.bank_id || '',
      account_number: llc.account_number || '',
      next_check_number: llc.next_check_number,
      issues_1099: llc.issues_1099 !== false,
    });
    // Load branding previews
    setLogoPreview(null);
    setSigPreview(null);
    if (llc.logo_path) {
      llcApi.getLogo(llc.id).then(res => {
        setLogoPreview(URL.createObjectURL(res.data));
      }).catch(() => {});
    }
    if (llc.signature_path) {
      llcApi.getSignature(llc.id).then(res => {
        setSigPreview(URL.createObjectURL(res.data));
      }).catch(() => {});
    }
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await llcApi.delete(id);
      toast.success('LLC deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete LLC');
    }
  };

  const handleDownloadReport = async (llc) => {
    try {
      toast.info(`Generating report for ${llc.company_name}...`);
      const response = await llcApi.getReportPdf(llc.id);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      toast.success('Report opened in new tab');
    } catch (error) {
      toast.error('Failed to generate report');
    }
  };

  const openNewDialog = () => {
    setEditingLLC(null);
    setFormData(initialFormData);
    setLogoPreview(null);
    setSigPreview(null);
    setDialogOpen(true);
  };

  const getBankName = (bankId) => {
    const bank = banks.find(b => b.id === bankId);
    return bank?.name || 'No bank assigned';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-slate-500">Loading LLCs...</div>
      </div>
    );
  }

  return (
    <div className="space-y-3 fade-in" data-testid="llc-management-page">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-lg font-semibold text-slate-900 tracking-tight">LLC Management</h1>
          <p className="text-slate-500 mt-1">Manage your LLCs and their banking information</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-white border border-slate-200 rounded-lg p-0.5 shadow-sm">
            <button
              onClick={() => handleViewMode('grid')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'grid' ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              data-testid="llc-grid-view-btn"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleViewMode('list')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'list' ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              data-testid="llc-list-view-btn"
            >
              <List className="w-4 h-4" />
            </button>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNewDialog} className="bg-slate-900 hover:bg-slate-800" data-testid="add-llc-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add LLC
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
            <DialogHeader>
              <DialogTitle>{editingLLC ? 'Edit LLC' : 'Add New LLC'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-slate-700 uppercase tracking-wide">Company Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label htmlFor="company_name">Company Name</Label>
                    <Input
                      id="company_name"
                      name="company_name"
                      value={formData.company_name}
                      onChange={handleInputChange}
                      required
                      data-testid="llc-company-name-input"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="address_line1">Address Line 1</Label>
                    <Input
                      id="address_line1"
                      name="address_line1"
                      value={formData.address_line1}
                      onChange={handleInputChange}
                      required
                      data-testid="llc-address-input"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="address_line2">Address Line 2</Label>
                    <Input
                      id="address_line2"
                      name="address_line2"
                      value={formData.address_line2}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div>
                    <Label htmlFor="city">City</Label>
                    <Input
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label htmlFor="state">State</Label>
                      <Input
                        id="state"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        required
                        maxLength={2}
                      />
                    </div>
                    <div>
                      <Label htmlFor="zip_code">ZIP Code</Label>
                      <Input
                        id="zip_code"
                        name="zip_code"
                        value={formData.zip_code}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="ein">EIN</Label>
                    <Input
                      id="ein"
                      name="ein"
                      value={formData.ein}
                      onChange={handleInputChange}
                      placeholder="XX-XXXXXXX"
                      required
                      data-testid="llc-ein-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="(XXX) XXX-XXXX"
                      data-testid="llc-phone-input"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-sm font-medium text-slate-700 uppercase tracking-wide">Bank Assignment</h3>
                {banks.length === 0 ? (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-800">
                      No banks available. Please add a bank in Bank Management first.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="bank_id">Select Bank *</Label>
                      <Select value={formData.bank_id} onValueChange={handleBankChange}>
                        <SelectTrigger data-testid="llc-bank-select">
                          <SelectValue placeholder="Select a bank..." />
                        </SelectTrigger>
                        <SelectContent>
                          {banks.map(bank => (
                            <SelectItem key={bank.id} value={bank.id}>
                              {bank.name} ({bank.outbound_routing || bank.routing_number})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {formData.bank_id && (
                        <div className="mt-2 p-3 bg-slate-50 rounded-lg text-sm text-slate-600">
                          {(() => {
                            const bank = banks.find(b => b.id === formData.bank_id);
                            return bank ? (
                              <>
                                <p><strong>Inbound Routing:</strong> {bank.inbound_routing || bank.routing_number}</p>
                                <p><strong>Outbound Routing:</strong> {bank.outbound_routing || bank.routing_number}</p>
                                {bank.rep_name && <p><strong>Rep:</strong> {bank.rep_name} {bank.rep_email && `(${bank.rep_email})`}</p>}
                              </>
                            ) : null;
                          })()}
                        </div>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="account_number">Account Number *</Label>
                      <Input
                        id="account_number"
                        name="account_number"
                        value={formData.account_number}
                        onChange={handleInputChange}
                        placeholder="Account number for this LLC"
                        required={banks.length > 0}
                        data-testid="llc-account-number-input"
                      />
                      <p className="text-xs text-slate-500 mt-1">Each LLC has its own account number at the bank</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <h3 className="text-sm font-medium text-slate-700 uppercase tracking-wide">Check Configuration</h3>
                <div>
                  <Label htmlFor="next_check_number">Next Check Number</Label>
                  <Input
                    id="next_check_number"
                    name="next_check_number"
                    type="number"
                    value={formData.next_check_number}
                    onChange={handleInputChange}
                    required
                    data-testid="llc-check-number-input"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-sm font-medium text-slate-700 uppercase tracking-wide">Tax Compliance</h3>
                <label className="flex items-center justify-between p-3 border rounded-lg cursor-pointer hover:bg-slate-50 transition-colors" data-testid="llc-issues-1099-toggle">
                  <div>
                    <div className="text-sm font-medium text-slate-800">Issue 1099 Forms</div>
                    <div className="text-xs text-slate-500 mt-0.5">When enabled, this LLC will generate 1099s for qualifying payees</div>
                  </div>
                  <div
                    className={`relative w-11 h-6 rounded-full transition-colors ${formData.issues_1099 ? 'bg-blue-600' : 'bg-slate-300'}`}
                    onClick={(e) => {
                      e.preventDefault();
                      setFormData(prev => ({ ...prev, issues_1099: !prev.issues_1099 }));
                    }}
                  >
                    <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${formData.issues_1099 ? 'translate-x-5' : 'translate-x-0'}`} />
                  </div>
                </label>
              </div>

              {editingLLC && (
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-slate-700 uppercase tracking-wide">Branding (Logo &amp; Signature)</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {/* Logo Upload */}
                    <div>
                      <Label className="mb-2 block">Company Logo</Label>
                      <div className="border-2 border-dashed border-slate-200 rounded-lg p-3 text-center min-h-[100px] flex flex-col items-center justify-center gap-2">
                        {logoPreview ? (
                          <>
                            <img src={logoPreview} alt="Logo" className="max-h-16 max-w-full object-contain" />
                            <button
                              type="button"
                              onClick={async () => {
                                try {
                                  await llcApi.deleteLogo(editingLLC.id);
                                  setLogoPreview(null);
                                  editingLLC.logo_path = '';
                                  toast.success('Logo removed');
                                  fetchData();
                                } catch { toast.error('Failed to remove logo'); }
                              }}
                              className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
                              data-testid="llc-remove-logo-btn"
                            >
                              <X className="w-3 h-3" /> Remove
                            </button>
                          </>
                        ) : (
                          <>
                            <Image className="w-6 h-6 text-slate-300" />
                            <span className="text-xs text-slate-400">No logo</span>
                          </>
                        )}
                        <label className="cursor-pointer text-xs text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1">
                          <Upload className="w-3 h-3" />
                          {uploadingLogo ? 'Uploading...' : 'Upload'}
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/webp"
                            className="hidden"
                            data-testid="llc-logo-upload-input"
                            disabled={uploadingLogo}
                            onChange={async (e) => {
                              const file = e.target.files[0];
                              if (!file) return;
                              setUploadingLogo(true);
                              try {
                                await llcApi.uploadLogo(editingLLC.id, file);
                                const res = await llcApi.getLogo(editingLLC.id);
                                setLogoPreview(URL.createObjectURL(res.data));
                                toast.success('Logo uploaded');
                                fetchData();
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Upload failed');
                              }
                              setUploadingLogo(false);
                              e.target.value = '';
                            }}
                          />
                        </label>
                      </div>
                    </div>
                    {/* Signature Upload */}
                    <div>
                      <Label className="mb-2 block">Signature Image</Label>
                      <div className="border-2 border-dashed border-slate-200 rounded-lg p-3 text-center min-h-[100px] flex flex-col items-center justify-center gap-2">
                        {sigPreview ? (
                          <>
                            <img src={sigPreview} alt="Signature" className="max-h-16 max-w-full object-contain" />
                            <button
                              type="button"
                              onClick={async () => {
                                try {
                                  await llcApi.deleteSignature(editingLLC.id);
                                  setSigPreview(null);
                                  editingLLC.signature_path = '';
                                  toast.success('Signature removed');
                                  fetchData();
                                } catch { toast.error('Failed to remove signature'); }
                              }}
                              className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
                              data-testid="llc-remove-sig-btn"
                            >
                              <X className="w-3 h-3" /> Remove
                            </button>
                          </>
                        ) : (
                          <>
                            <Image className="w-6 h-6 text-slate-300" />
                            <span className="text-xs text-slate-400">No signature</span>
                          </>
                        )}
                        <label className="cursor-pointer text-xs text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1">
                          <Upload className="w-3 h-3" />
                          {uploadingSig ? 'Uploading...' : 'Upload'}
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/webp"
                            className="hidden"
                            data-testid="llc-sig-upload-input"
                            disabled={uploadingSig}
                            onChange={async (e) => {
                              const file = e.target.files[0];
                              if (!file) return;
                              setUploadingSig(true);
                              try {
                                await llcApi.uploadSignature(editingLLC.id, file);
                                const res = await llcApi.getSignature(editingLLC.id);
                                setSigPreview(URL.createObjectURL(res.data));
                                toast.success('Signature uploaded');
                                fetchData();
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Upload failed');
                              }
                              setUploadingSig(false);
                              e.target.value = '';
                            }}
                          />
                        </label>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-slate-400">Logo appears on the far right of the check. Signature appears on the signature line in dark blue. PNG with transparent background recommended.</p>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting || (banks.length > 0 && !formData.bank_id)} data-testid="llc-submit-btn">
                  {submitting ? 'Saving...' : editingLLC ? 'Update LLC' : 'Add LLC'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {llcs.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <Building2 className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-1">No LLCs Yet</h3>
              <p className="text-slate-500">Add your first LLC to get started.</p>
            </div>
          </CardContent>
        </Card>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {llcs.map((llc) => (
            <Card key={llc.id} className="relative group" data-testid={`llc-card-${llc.id}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center justify-between">
                  <span className="truncate">
                    <button
                      className="text-blue-700 hover:text-blue-900 hover:underline cursor-pointer text-left font-bold text-base truncate"
                      onClick={(e) => { e.stopPropagation(); openLlcChecks(llc.id, llc.company_name); }}
                      data-testid={`llc-name-link-${llc.id}`}
                    >
                      {llc.company_name}
                    </button>
                  </span>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(llc)} data-testid="edit-llc-btn">
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" data-testid="delete-llc-btn">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete LLC?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete {llc.company_name}? This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDelete(llc.id)} className="bg-red-600 hover:bg-red-700">
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-sm text-slate-600 space-y-1">
                  <p>{llc.address_line1}</p>
                  {llc.address_line2 && <p>{llc.address_line2}</p>}
                  <p>{llc.city}, {llc.state} {llc.zip_code}</p>
                  <p className="text-slate-400">EIN: {llc.ein}</p>
                  {llc.issues_1099 !== false && (
                    <p className="flex items-center gap-1 text-xs text-emerald-600 mt-1" data-testid={`llc-1099-badge-${llc.id}`}>
                      <FileCheck2 className="w-3 h-3" /> 1099 Enabled
                    </p>
                  )}
                  {llc.issues_1099 === false && (
                    <p className="flex items-center gap-1 text-xs text-slate-400 mt-1" data-testid={`llc-1099-badge-${llc.id}`}>
                      <FileCheck2 className="w-3 h-3" /> 1099 Disabled
                    </p>
                  )}
                </div>
                <div className="mt-3 pt-3 border-t border-slate-100">
                  <p className="text-sm font-medium text-slate-700">
                    {llc.bank_id ? getBankName(llc.bank_id) : llc.bank_name || 'No bank assigned'}
                  </p>
                  {llc.account_number && (
                    <p className="text-xs text-slate-500 font-mono">
                      Account: {llc.account_number}
                    </p>
                  )}
                  <p className="text-xs text-slate-400 font-mono mt-1">
                    Next Check: {llc.next_check_number}
                  </p>
                  <button
                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium mt-2"
                    onClick={() => openLlcChecks(llc.id, llc.company_name)}
                    data-testid={`llc-check-register-${llc.id}`}
                  >
                    <ClipboardList className="w-3.5 h-3.5" /> Check Register
                  </button>
                  <button
                    className="flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-800 font-medium mt-1"
                    onClick={() => handleDownloadReport(llc)}
                    data-testid={`llc-pdf-report-${llc.id}`}
                  >
                    <FileDown className="w-3.5 h-3.5" /> PDF Report
                  </button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-slate-200">
          <CardContent className="p-0">
            <div className="overflow-x-auto max-h-[calc(100vh-220px)] overflow-y-auto">
              <table className="data-table">
                <thead className="sticky top-0 z-10">
                  <tr style={{ backgroundColor: '#1e293b', color: '#ffffff' }}>
                    <th className="cursor-pointer select-none" onClick={() => handleSort('company_name')} style={{ color: '#ffffff', borderBottom: 'none' }}>
                      <span className="flex items-center gap-1.5">Company Name <SortIcon field="company_name" /></span>
                    </th>
                    <th className="cursor-pointer select-none" onClick={() => handleSort('address_line1')} style={{ color: '#ffffff', borderBottom: 'none' }}>
                      <span className="flex items-center gap-1.5">Address <SortIcon field="address_line1" /></span>
                    </th>
                    <th className="cursor-pointer select-none" onClick={() => handleSort('ein')} style={{ color: '#ffffff', borderBottom: 'none' }}>
                      <span className="flex items-center gap-1.5">EIN <SortIcon field="ein" /></span>
                    </th>
                    <th style={{ color: '#ffffff', borderBottom: 'none' }}>Phone</th>
                    <th className="cursor-pointer select-none" onClick={() => handleSort('bank_name_display')} style={{ color: '#ffffff', borderBottom: 'none' }}>
                      <span className="flex items-center gap-1.5">Bank <SortIcon field="bank_name_display" /></span>
                    </th>
                    <th style={{ color: '#ffffff', borderBottom: 'none' }}>Account #</th>
                    <th className="cursor-pointer select-none text-right" onClick={() => handleSort('next_check_number')} style={{ color: '#ffffff', borderBottom: 'none' }}>
                      <span className="flex items-center justify-end gap-1.5">Next Check <SortIcon field="next_check_number" /></span>
                    </th>
                    <th className="text-right" style={{ color: '#ffffff', borderBottom: 'none' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedLLCs.map((llc) => (
                    <tr key={llc.id} data-testid={`llc-row-${llc.id}`}>
                      <td className="font-semibold">
                        <div className="flex items-center gap-2">
                          <button
                            className="text-blue-700 hover:text-blue-900 hover:underline cursor-pointer text-left"
                            onClick={() => openLlcChecks(llc.id, llc.company_name)}
                            data-testid={`llc-name-link-list-${llc.id}`}
                          >
                            {llc.company_name}
                          </button>
                          {llc.issues_1099 !== false ? (
                            <span className="text-[10px] text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded font-medium" data-testid={`llc-1099-badge-list-${llc.id}`}>1099</span>
                          ) : (
                            <span className="text-[10px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded font-medium" data-testid={`llc-1099-badge-list-${llc.id}`}>No 1099</span>
                          )}
                        </div>
                      </td>
                      <td>{llc.address_line1}, {llc.city}, {llc.state} {llc.zip_code}</td>
                      <td>{llc.ein}</td>
                      <td>{formatPhoneNumber(llc.phone) || '-'}</td>
                      <td>{llc.bank_id ? getBankName(llc.bank_id) : llc.bank_name || '-'}</td>
                      <td>{llc.account_number || '-'}</td>
                      <td className="text-right">{llc.next_check_number}</td>
                      <td className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openLlcChecks(llc.id, llc.company_name)} title="Check Register" data-testid={`llc-check-register-list-${llc.id}`}>
                            <ClipboardList className="w-4 h-4 text-blue-600" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDownloadReport(llc)} title="PDF Report" data-testid={`llc-pdf-report-list-${llc.id}`}>
                            <FileDown className="w-4 h-4 text-emerald-600" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(llc)} data-testid="edit-llc-btn">
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" data-testid="delete-llc-btn">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete LLC?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete {llc.company_name}? This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleDelete(llc.id)} className="bg-red-600 hover:bg-red-700">
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* LLC Checks Modal */}
      <Dialog open={showChecks} onOpenChange={setShowChecks}>
        <DialogContent className="max-w-4xl max-h-[80vh]" data-testid="llc-checks-modal">
          <DialogHeader>
            <DialogTitle>Check Register — {checksLlcName}</DialogTitle>
          </DialogHeader>
          {checksLoading ? (
            <div className="py-8 text-center text-slate-500">Loading payments...</div>
          ) : sortedLlcChecks.length === 0 ? (
            <div className="py-8 text-center text-slate-500">No payments found for this LLC.</div>
          ) : (
            <div className="overflow-y-auto" style={{ maxHeight: '60vh' }}>
              <table className="data-table w-full">
                <thead className="sticky top-0 z-10 bg-white">
                  <tr>
                    <th>Type</th>
                    <ChecksSortHeader col="ref">Ref #</ChecksSortHeader>
                    <ChecksSortHeader col="date">Date</ChecksSortHeader>
                    <ChecksSortHeader col="payee">Payee</ChecksSortHeader>
                    <ChecksSortHeader col="memo">Memo</ChecksSortHeader>
                    <ChecksSortHeader col="amount" className="text-right">Amount</ChecksSortHeader>
                    <ChecksSortHeader col="status">Status</ChecksSortHeader>
                  </tr>
                </thead>
                <tbody>
                  {sortedLlcChecks.map((c, i) => (
                    <tr key={i} className={c.status === 'voided' ? 'opacity-50 line-through' : ''}>
                      <td>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded ${c.type === 'check' ? 'bg-blue-50 text-blue-700' : 'bg-orange-50 text-orange-700'}`}>
                          {c.type === 'check' ? 'Check' : 'Wire'}
                        </span>
                      </td>
                      <td className="font-mono text-sm">{c.type === 'check' ? c.ref : '—'}</td>
                      <td className="text-sm">{c.date ? new Date(c.date).toLocaleDateString() : '—'}</td>
                      <td className="text-sm font-medium">{c.payee}</td>
                      <td className="text-sm text-slate-600">{c.memo || '—'}</td>
                      <td className="text-sm text-right font-mono">${(c.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                      <td>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                          c.status === 'printed' ? 'bg-green-50 text-green-700' :
                          c.status === 'voided' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'
                        }`}>
                          {c.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="text-sm text-slate-500 mt-3 px-2">
                {sortedLlcChecks.length} payment{sortedLlcChecks.length !== 1 ? 's' : ''} — Total: ${sortedLlcChecks.filter(c => c.status !== 'voided').reduce((s, c) => s + (c.amount || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2 })} (excluding voided)
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
